import { v4 as uuidv4 } from 'uuid';
import { EventContentArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import { toZonedTime } from "date-fns-tz";
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import scrollGridPlugin from '@fullcalendar/scrollgrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import { format, isDate } from 'date-fns';
import { plugin as dayjsTimeZonePlugin } from 'fullcalendar-plugin-dayjs-timezone';
import { ArrowLeft, ArrowRight, CalendarIcon, FilterIcon, Loader2, Pencil, Plus } from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import EventPopover from '~/components/treatment/appointment/calendar/popover/EventPopover';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '~/components/ui/alert-dialog';
import { AppointmentFilters, CalendarViewMode, DateRange, Equipment, Practitioner, Room, useAppointmentsCalendarStore } from '~/stores/appointments-calendar-store';
import Content from '../components/layout/Content';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { keepPreviousData } from '@tanstack/react-query';
import { de } from 'date-fns/locale';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import { CreateOrModifyAppointmentForm } from '~/components/appointments/CreateAppointmentForm';
import { TREATMENT_COLORS } from '~/lib/constants';
import { CalendarScheduleTimeBlockEntry } from '~/components/calendar/CalendarScheduleTimeBlockEntry';
import { ScheduleEntryForm } from '~/components/practitioners/new-schedule-manager/ScheduleEntryForm';
import { Checkbox } from '~/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '~/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '~/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import LocalDateTime, { IANATimezone } from '~/lib/localdatetime';
import { useCreateOrModifyAppointmentStore } from '~/stores/create-appointment-store';
import { useScheduleEditorModalStore } from '~/stores/schedule-editor-modal-store';
import { useUserStore } from '~/stores/user-store';
import { AppointmentStatus } from '~/types/server-types/AppointmentStatus';
import { UserRole } from '~/types/server-types/UserRole';
import { ResourceOrderDialog } from '~/components/appointments/ResourceOrderDialog';

const CalendarDatePickerWithRange = () => {
    const { selectedDateRange, setSelectedDateRange, viewMode } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedDateRange: state.selectedDateRange,
            setSelectedDateRange: state.setSelectedDateRange,
            viewMode: state.viewMode,
        }))
    );

    // Convert store date format to DateRange format
    const dates: LocalDateTime[] = useMemo(() => {
        const dateArray: LocalDateTime[] = [];
        let currentDate = selectedDateRange.startDate.clone();

        while (currentDate <= selectedDateRange.endDate) {
            dateArray.push(currentDate);
            currentDate = currentDate.addDays(1);
        }

        return dateArray;
    }, [selectedDateRange]);

    // Update the store when date changes
    const handleDateChange = useCallback(
        (newDate: Date | LocalDateTime | undefined) => {
            if (!newDate) {
                return;
            }

            let localNewDate: LocalDateTime;

            if (isDate(newDate)) {
                const newDateStr = format(newDate, "yyyy-MM-dd");
                console.log("handleDateChange", newDateStr);
                localNewDate = new LocalDateTime(`${newDateStr} 00:00`, "Europe/Berlin");
            } else {
                localNewDate = newDate;
            }


            let startDate = localNewDate;
            let endDate: LocalDateTime;

            switch (viewMode) {
                case 'dayGridMonth':
                    // Select the entire month
                    startDate = startDate.setDayOfMonth(1);
                    endDate = startDate.clone().addMonths(1);
                    break;
                case 'timeGridWeek':
                    // Select the entire week (Monday to Sunday)
                    startDate = startDate.setDayOfWeek("monday");
                    endDate = startDate.clone().addDays(6);
                    // const day = startDate.getDay();
                    // const diff = day === 0 ? 6 : day - 1; // Adjust for Monday as first day
                    // startDate = new Date(startDate.setDate(startDate.getDate() - diff));
                    // endDate = new Date(startDate);
                    // endDate.setDate(startDate.getDate() + 6);
                    break;
                case 'timeGridThreeDays':
                    // Select the selected date and 2 days after
                    endDate = startDate.clone().addDays(2);
                    break;
                case 'resourceTimeGridDay':
                    // Select just the date
                    endDate = startDate;
                    break;
                default:
                    endDate = startDate;
                    break;
            }

            setSelectedDateRange({
                startDate,
                endDate,
            });
        },
        [setSelectedDateRange, viewMode]
    );

    // Handle navigation with arrow buttons
    const handlePrevDay = useCallback(() => {
        let dayOffset = 1;
        if (viewMode === 'timeGridThreeDays') {
            dayOffset = 3;
        } else if (viewMode === 'timeGridWeek') {
            dayOffset = 7;
        } else if (viewMode === 'dayGridMonth') {
            dayOffset = 30;
        }

        const newDate = selectedDateRange.startDate.clone().addDays(-dayOffset);
        handleDateChange(newDate);
    }, [selectedDateRange.startDate, handleDateChange]);

    const handleNextDay = useCallback(() => {
        let dayOffset = 1;
        if (viewMode === 'timeGridThreeDays') {
            dayOffset = 3;
        } else if (viewMode === 'timeGridWeek') {
            dayOffset = 7;
        } else if (viewMode === 'dayGridMonth') {
            dayOffset = 30;
        }

        const newDate = selectedDateRange.startDate.clone().addDays(dayOffset);
        handleDateChange(newDate);
    }, [selectedDateRange.startDate, handleDateChange]);

    return (
        <div className={cn('grid gap-2')}>
            <div className='flex items-center'>
                <Button variant='outline' size='icon' onClick={handlePrevDay} className='mr-2'>
                    <ArrowLeft className='h-4 w-4' />
                </Button>

                <Popover>
                    <PopoverTrigger asChild>
                        <Button
                            id='date'
                            variant={'outline'}
                            className={cn('w-[300px] justify-start text-left font-normal', !selectedDateRange.startDate && 'text-muted-foreground')}
                        >
                            <CalendarIcon className='mr-2 h-4 w-4' />
                            {selectedDateRange.startDate ? (
                                selectedDateRange.endDate && selectedDateRange.startDate.valueOf() !== selectedDateRange.endDate.valueOf() ? (
                                    <>
                                        {format(toZonedTime(new Date(selectedDateRange.startDate.getDisplayDate()), "Europe/Berlin"), 'dd.MM.yyyy', { locale: de })}
                                        -
                                        {format(toZonedTime(new Date(selectedDateRange.endDate.getDisplayDate()), "Europe/Berlin"), 'dd.MM.yyyy', { locale: de })}
                                    </>
                                ) : (
                                    format(toZonedTime(new Date(selectedDateRange.startDate.getDisplayDate()), "Europe/Berlin"), 'EEEE, dd. MMMM yyyy', { locale: de })
                                )
                            ) : (
                                <span>Pick a date</span>
                            )}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className='w-auto p-0' align='start'>
                        <Calendar
                            mode='multiple'
                            locale={de}
                            defaultMonth={new Date(dates[0].getDisplayDateTime())}
                            selected={dates.map((d) => toZonedTime(new Date(d.getDisplayDate()), "Europe/Berlin"))}

                            onDayClick={handleDateChange}
                            numberOfMonths={2}
                            weekStartsOn={1} // Monday as first day of week
                        />
                    </PopoverContent>
                </Popover>

                <Button variant='outline' size='icon' onClick={handleNextDay} className='ml-2'>
                    <ArrowRight className='h-4 w-4' />
                </Button>
            </div>
        </div>
    );
};

const TodayButton = () => {
    const { selectedDateRange, setSelectedDateRange } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedDateRange: state.selectedDateRange,
            setSelectedDateRange: state.setSelectedDateRange,
        }))
    );

    const handleTodayClick = () => {
        const today = LocalDateTime.now("Europe/Berlin");

        // Calculate the current duration in milliseconds
        const currentDurationMs = selectedDateRange.endDate.valueOf() - selectedDateRange.startDate.valueOf();
        const currentDurationDays = Math.round(currentDurationMs / 1000 / 60 / 60 / 24);

        // Create new end date by adding the same duration to today
        const newEndDate = today.clone().addDays(currentDurationDays);

        // Update the date range
        setSelectedDateRange({
            startDate: today,
            endDate: newEndDate,
        });
    };

    return (
        <Button variant='outline' onClick={handleTodayClick} className='w-24'>
            Heute
        </Button>
    );
};

type ViewModeSelectorProps = {
    calendarRef: React.RefObject<FullCalendar | null>;
};
const ViewModeSelector = ({ calendarRef }: ViewModeSelectorProps) => {
    const { viewMode, setViewMode, selectedDateRange, setSelectedDateRange } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            viewMode: state.viewMode,
            setViewMode: state.setViewMode,
            selectedDateRange: state.selectedDateRange,
            setSelectedDateRange: state.setSelectedDateRange,
        }))
    );

    const handleViewModeChange = (value: string) => {
        const newViewMode = value as CalendarViewMode;
        setViewMode(newViewMode);

        // Get the current start date as reference point
        const currentStartDate = selectedDateRange.startDate;
        let newStartDate = currentStartDate.clone();
        let newEndDate: LocalDateTime;

        // Calculate new date range based on selected view mode
        switch (newViewMode) {
            case 'dayGridMonth':
                // Select the entire month
                newStartDate = newStartDate.setDayOfMonth(1);
                newEndDate = newStartDate.clone().addMonths(1);
                break;
            case 'timeGridWeek':
                // Select the entire week (Monday to Sunday)
                newStartDate = newStartDate.setDayOfWeek("monday");
                newEndDate = newStartDate.clone().addDays(6);
                break;
            case 'timeGridThreeDays':
                // Select the current date and 2 days after
                newEndDate = newStartDate.clone().addDays(2);
                break;
            case 'resourceTimeGridDay':
                // Select only the current day
                newEndDate = newStartDate.clone();
                break;
            default:
                newEndDate = newStartDate.clone();
                break;
        }

        // Update the date range in the store
        setSelectedDateRange({
            startDate: newStartDate,
            endDate: newEndDate,
        });

        // Update the calendar view
        if (calendarRef.current) {
            calendarRef.current.getApi().changeView(value);
            calendarRef.current.getApi().gotoDate(newStartDate.getDisplayDate());
        }
    };

    return (
        <Tabs value={viewMode} onValueChange={handleViewModeChange}>
            <TabsList className='grid grid-cols-3'>
                <TabsTrigger value='resourceTimeGridDay'>Tag</TabsTrigger>
                <TabsTrigger value='timeGridThreeDays'>3 Tage</TabsTrigger>
                <TabsTrigger value='timeGridWeek'>Woche</TabsTrigger>
                {/* <TabsTrigger value='dayGridMonth'>Monat</TabsTrigger> */}
            </TabsList>
        </Tabs>
    );
};

const CalendarResourcesFilter = () => {
    const { selectedStore, setSelectedStore } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
            setSelectedStore: state.setSelectedStore,
        }))
    );
    const {
        appointmentFilters,
        roomFilters,
        equipmentFilters,
        practitionerFilters,
        allRooms,
        allEquipment,
        allPractitioners,
        setAppointmentFilters,
        setRoomFilters,
        setEquipmentFilters,
        setPractitionerFilters,
    } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            appointmentFilters: state.appointmentFilters,
            roomFilters: state.roomFilters,
            equipmentFilters: state.equipmentFilters,
            practitionerFilters: state.practitionerFilters,
            allRooms: state.allRooms,
            allEquipment: state.allEquipment,
            allPractitioners: state.allPractitioners,
            setAppointmentFilters: state.setAppointmentFilters,
            setRoomFilters: state.setRoomFilters,
            setEquipmentFilters: state.setEquipmentFilters,
            setPractitionerFilters: state.setPractitionerFilters,
        }))
    );

    // Local state for temporary filters (before applying)
    const [tempAppointmentFilters, setTempAppointmentFilters] = useState<AppointmentFilters>(appointmentFilters);
    const [tempRoomFilters, setTempRoomFilters] = useState<Set<string>>(roomFilters);
    const [tempEquipmentFilters, setTempEquipmentFilters] = useState<Set<string>>(equipmentFilters);
    const [tempPractitionerFilters, setTempPractitionerFilters] = useState<Set<string>>(practitionerFilters);
    const [open, setOpen] = useState(false);

    // Reset temporary filters when dialog opens
    useEffect(() => {
        if (open) {
            setTempAppointmentFilters(appointmentFilters);
            setTempRoomFilters(roomFilters);
            setTempEquipmentFilters(equipmentFilters);
            setTempPractitionerFilters(practitionerFilters);
        }
    }, [open, appointmentFilters, roomFilters, equipmentFilters, practitionerFilters]);

    // Apply filters when dialog is closed with "Apply" button
    const handleApplyFilters = () => {
        setAppointmentFilters(tempAppointmentFilters);
        setRoomFilters(tempRoomFilters);
        setEquipmentFilters(tempEquipmentFilters);
        setPractitionerFilters(tempPractitionerFilters);
        setOpen(false);
    };

    console.log("tempPractitionerFilters")
    console.log(tempPractitionerFilters);

    console.log("practitionerFilters")
    console.log(practitionerFilters)

    // Toggle a filter value
    const toggleFilter = (currentFilters: Set<string>, setFilters: (value: Set<string>) => void, id: string) => {
        const newFilters = new Set(currentFilters);
        if (newFilters.has(id)) {
            newFilters.delete(id);
        } else {
            newFilters.add(id);
        }
        setFilters(newFilters);
    };

    // Toggle all items in a category
    const toggleCategoryItems = (currentFilters: Set<string>, setFilters: (value: Set<string>) => void, itemIds: string[]) => {
        const allIncluded = itemIds.every((id) => currentFilters.has(id));
        const newFilters = new Set(currentFilters);

        if (allIncluded) {
            // Remove all items in this category
            itemIds.forEach((id) => newFilters.delete(id));
        } else {
            // Add all items in this category that aren't already included
            itemIds.forEach((id) => newFilters.add(id));
        }

        setFilters(newFilters);
    };

    // Group rooms by category
    const roomsByCategory = useMemo(() => {
        const grouped: Record<string, Room[]> = {};
        if (!selectedStore) {
            return grouped;
        }

        // Add "No Category" group
        grouped['No Category'] = [];

        allRooms.filter((room) => room.storeId === selectedStore.id).forEach((room) => {
            const categoryName = room.room_category?.name || 'No Category';
            if (!grouped[categoryName]) {
                grouped[categoryName] = [];
            }
            grouped[categoryName].push(room);
        });

        return grouped;
    }, [allRooms, selectedStore]);

    // Group equipment by category
    const equipmentByCategory = useMemo(() => {
        const grouped: Record<string, Equipment[]> = {};
        if (!selectedStore) {
            return grouped;
        }

        // Add "No Category" group
        grouped['No Category'] = [];

        allEquipment.filter((equipment) => equipment.storeId === selectedStore.id).forEach((equipment) => {
            const categoryName = equipment.equipment_category?.name || 'No Category';
            if (!grouped[categoryName]) {
                grouped[categoryName] = [];
            }
            grouped[categoryName].push(equipment);
        });

        return grouped;
    }, [allEquipment, selectedStore]);

    // Group practitioners by category
    const practitionersByCategory = useMemo(() => {
        const grouped: Record<string, Practitioner[]> = {};
        if (!selectedStore) {
            return grouped;
        }

        // Add "No Category" group
        grouped['No Category'] = [];

        allPractitioners.filter((practitioner) => practitioner.storeIds.includes(selectedStore.id)).forEach((practitioner) => {
            const categoryName = practitioner.practitioner_category?.name || 'No Category';
            if (!grouped[categoryName]) {
                grouped[categoryName] = [];
            }
            grouped[categoryName].push(practitioner);
        });

        return grouped;
    }, [allPractitioners, selectedStore]);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant='outline' size='icon' className='relative'>
                    <FilterIcon className='h-4 w-4' />
                    {(roomFilters.size > 0 || equipmentFilters.size > 0 || practitionerFilters.size > 0) && (
                        <div className='absolute -top-2 -right-2 h-5 w-5 rounded-full bg-primary-500 flex items-center justify-center text-[10px] text-white font-medium'>
                            {roomFilters.size + equipmentFilters.size + practitionerFilters.size}
                        </div>
                    )}
                </Button>
            </DialogTrigger>
            <DialogContent className='sm:max-w-[600px]'>
                <DialogHeader>
                    <DialogTitle>Filter</DialogTitle>
                    <DialogDescription>Wähle, was du im Kalender anzeigen möchtest.</DialogDescription>
                </DialogHeader>

                <div className='flex flex-row gap-8'>
                    <Tabs defaultValue='appointments' className='mt-4'>
                        <TabsList className='grid grid-cols-3'>
                            <TabsTrigger value='rooms'>Räume</TabsTrigger>
                            <TabsTrigger value='equipment'>Geräte</TabsTrigger>
                            <TabsTrigger value='practitioners'>Behandler</TabsTrigger>
                        </TabsList>

                        <TabsContent value='rooms' className='max-h-[500px] overflow-y-auto'>
                            {Object.keys(roomsByCategory).length > 0 ? (
                                <div className='space-y-4 mt-2'>
                                    {Object.entries(roomsByCategory).map(([categoryName, rooms]) => {
                                        if (rooms.length === 0) return null;

                                        const categoryRoomIds = rooms.map((room) => room.id.toString());
                                        const allCategoryRoomsSelected = categoryRoomIds.every((id) => tempRoomFilters.has(id));

                                        return (
                                            <div key={categoryName} className='space-y-2'>
                                                <div className='flex items-center space-x-2 font-medium'>
                                                    <Checkbox
                                                        id={`room-category-${categoryName}`}
                                                        checked={allCategoryRoomsSelected && rooms.length > 0}
                                                        onCheckedChange={() => toggleCategoryItems(tempRoomFilters, setTempRoomFilters, categoryRoomIds)}
                                                    />
                                                    <label htmlFor={`room-category-${categoryName}`} className='text-sm font-bold'>
                                                        {categoryName} ({rooms.length})
                                                    </label>
                                                </div>

                                                <div className='pl-6 space-y-2'>
                                                    {rooms.map((room) => {
                                                        const roomLabel = room.name;
                                                        const isTruncated = roomLabel.length > 64;
                                                        const displayLabel = isTruncated ? `${roomLabel.substring(0, 61)}...` : roomLabel;

                                                        return (
                                                            <div key={room.id} className='flex items-center space-x-2'>
                                                                <Checkbox
                                                                    id={`room-${room.id}`}
                                                                    checked={tempRoomFilters.has(room.id.toString())}
                                                                    onCheckedChange={() => toggleFilter(tempRoomFilters, setTempRoomFilters, room.id.toString())}
                                                                />
                                                                <div className='relative'>
                                                                    <label
                                                                        htmlFor={`room-${room.id}`}
                                                                        className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 wrap-break-word max-w-[380px] inline-block'
                                                                        title={isTruncated ? roomLabel : undefined}
                                                                    >
                                                                        {displayLabel}
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className='text-sm text-muted-foreground mt-2'>No rooms available</p>
                            )}
                        </TabsContent>

                        <TabsContent value='equipment' className='max-h-[500px] overflow-y-auto'>
                            {Object.keys(equipmentByCategory).length > 0 ? (
                                <div className='space-y-4 mt-2'>
                                    {Object.entries(equipmentByCategory).map(([categoryName, equipmentItems]) => {
                                        if (equipmentItems.length === 0) return null;

                                        const categoryEquipmentIds = equipmentItems.map((item) => item.id.toString());
                                        const allCategoryEquipmentSelected = categoryEquipmentIds.every((id) => tempEquipmentFilters.has(id));

                                        return (
                                            <div key={categoryName} className='space-y-2'>
                                                <div className='flex items-center space-x-2 font-medium'>
                                                    <Checkbox
                                                        id={`equipment-category-${categoryName}`}
                                                        checked={allCategoryEquipmentSelected && equipmentItems.length > 0}
                                                        onCheckedChange={() =>
                                                            toggleCategoryItems(tempEquipmentFilters, setTempEquipmentFilters, categoryEquipmentIds)
                                                        }
                                                    />
                                                    <label htmlFor={`equipment-category-${categoryName}`} className='text-sm font-bold'>
                                                        {categoryName} ({equipmentItems.length})
                                                    </label>
                                                </div>

                                                <div className='pl-6 space-y-2'>
                                                    {equipmentItems.map((equipment) => {
                                                        const equipmentLabel = equipment.name;
                                                        const isTruncated = equipmentLabel.length > 64;
                                                        const displayLabel = isTruncated ? `${equipmentLabel.substring(0, 61)}...` : equipmentLabel;

                                                        return (
                                                            <div key={equipment.id} className='flex items-center space-x-2'>
                                                                <Checkbox
                                                                    id={`equipment-${equipment.id}`}
                                                                    checked={tempEquipmentFilters.has(equipment.id.toString())}
                                                                    onCheckedChange={() =>
                                                                        toggleFilter(tempEquipmentFilters, setTempEquipmentFilters, equipment.id.toString())
                                                                    }
                                                                />
                                                                <div className='relative'>
                                                                    <label
                                                                        htmlFor={`equipment-${equipment.id}`}
                                                                        className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 wrap-break-word max-w-[380px] inline-block'
                                                                        title={isTruncated ? equipmentLabel : undefined}
                                                                    >
                                                                        {displayLabel}
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className='text-sm text-muted-foreground mt-2'>No equipment available</p>
                            )}
                        </TabsContent>

                        <TabsContent value='practitioners' className='max-h-[500px] overflow-y-auto'>
                            {Object.keys(practitionersByCategory).length > 0 ? (
                                <div className='space-y-4 mt-2'>
                                    {Object.entries(practitionersByCategory).map(([categoryName, practitioners]) => {
                                        if (practitioners.length === 0) return null;

                                        const categoryPractitionerIds = practitioners.map((practitioner) => practitioner.id.toString());
                                        const allCategoryPractitionersSelected = categoryPractitionerIds.every((id) => tempPractitionerFilters.has(id));

                                        return (
                                            <div key={categoryName} className='space-y-2'>
                                                <div className='flex items-center space-x-2 font-medium'>
                                                    <Checkbox
                                                        id={`practitioner-category-${categoryName}`}
                                                        checked={allCategoryPractitionersSelected && practitioners.length > 0}
                                                        onCheckedChange={() =>
                                                            toggleCategoryItems(tempPractitionerFilters, setTempPractitionerFilters, categoryPractitionerIds)
                                                        }
                                                    />
                                                    <label htmlFor={`practitioner-category-${categoryName}`} className='text-sm font-bold'>
                                                        {categoryName} ({practitioners.length})
                                                    </label>
                                                </div>

                                                <div className='pl-6 space-y-2'>
                                                    {practitioners.map((practitioner) => {
                                                        const practitionerLabel = `${practitioner.firstName} ${practitioner.lastName}`;
                                                        const isTruncated = practitionerLabel.length > 64;
                                                        const displayLabel = isTruncated ? `${practitionerLabel.substring(0, 61)}...` : practitionerLabel;

                                                        return (
                                                            <div key={practitioner.id} className='flex items-center space-x-2'>
                                                                <Checkbox
                                                                    id={`practitioner-${practitioner.id}`}
                                                                    checked={tempPractitionerFilters.has(practitioner.id.toString())}
                                                                    onCheckedChange={() =>
                                                                        toggleFilter(
                                                                            tempPractitionerFilters,
                                                                            setTempPractitionerFilters,
                                                                            practitioner.id.toString()
                                                                        )
                                                                    }
                                                                />
                                                                <div className='relative'>
                                                                    <label
                                                                        htmlFor={`practitioner-${practitioner.id}`}
                                                                        className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 wrap-break-word max-w-[380px] inline-block'
                                                                        title={isTruncated ? practitionerLabel : undefined}
                                                                    >
                                                                        {displayLabel}
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}

                                    <div key={'other'} className='space-y-2'>
                                        <div className='flex items-center space-x-2 font-medium'>
                                            <label htmlFor={`practitioner-category-other`} className='text-sm font-bold'>
                                                Other (2)
                                            </label>
                                        </div>

                                        <div className='pl-6 space-y-2'>
                                            {[{ label: "Nicht zugewiesen", id: 'unassigned' }, { label: "Einwirkzeit/Durchlaufzeit", id: 'waiting' }].map((practitioner) => {
                                                return (
                                                    <div key={practitioner.id} className='flex items-center space-x-2'>
                                                        <Checkbox
                                                            id={`practitioner-${practitioner.id}`}
                                                            checked={tempPractitionerFilters.has(practitioner.id.toString())}
                                                            onCheckedChange={() =>
                                                                toggleFilter(
                                                                    tempPractitionerFilters,
                                                                    setTempPractitionerFilters,
                                                                    practitioner.id.toString()
                                                                )
                                                            }
                                                        />
                                                        <div className='relative'>
                                                            <label
                                                                htmlFor={`practitioner-${practitioner.id}`}
                                                                className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 wrap-break-word max-w-[380px] inline-block'
                                                            >
                                                                {practitioner.label}
                                                            </label>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className='text-sm text-muted-foreground mt-2'>No practitioners available</p>
                            )}
                        </TabsContent>
                    </Tabs>
                    <div className='flex flex-col gap-2'>
                        <span>Status</span>
                        <div className='space-y-2'>
                            {Object.values(AppointmentStatus).map((status) => {
                                const label = (status[0].toUpperCase() + status.slice(1).toLowerCase()).replaceAll("_", " ");
                                const value = status;

                                return (
                                    <div key={`status-${value}`} className='flex items-center space-x-2'>
                                        <Checkbox
                                            id={`appt-status-${value}`}
                                            checked={tempAppointmentFilters.status.has(value)}
                                            onCheckedChange={(checked) => {
                                                const newAppointmentStatusFilter: Set<AppointmentStatus> = new Set<AppointmentStatus>(tempAppointmentFilters.status);
                                                if (checked === 'indeterminate' || !checked) {
                                                    newAppointmentStatusFilter.delete(value);
                                                } else {
                                                    newAppointmentStatusFilter.add(value);
                                                }
                                                setTempAppointmentFilters((prev) => ({ ...prev, status: newAppointmentStatusFilter }))
                                            }}
                                        />
                                        <div className='relative'>
                                            <label
                                                htmlFor={`room-${value}`}
                                                className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 wrap-break-word max-w-[380px] inline-block'
                                                title={label}
                                            >
                                                {label}
                                            </label>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <DialogFooter className='flex justify-between sm:justify-between'>
                    <div className='flex gap-2'>
                        <Button
                            variant='outline'
                            onClick={() => {
                                setTempAppointmentFilters((prev) => ({ ...prev, status: new Set() }))
                                setTempRoomFilters(new Set());
                                setTempEquipmentFilters(new Set());
                                setTempPractitionerFilters(new Set());
                            }}
                        >
                            Filter zurücksetzen
                        </Button>
                        <Button variant='outline' onClick={() => setOpen(false)}>
                            Abbrechen
                        </Button>
                    </div>
                    <Button onClick={handleApplyFilters}>Filter anwenden</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

type IncomingAppointmentRequestsWidgetProps = {
    storeId: number;
    calendarRef: React.RefObject<FullCalendar | null>;
}

const IncomingAppointmentRequestsWidget = ({ storeId, calendarRef }: IncomingAppointmentRequestsWidgetProps) => {
    const { setMode, setAppointmentId, setNotifyCustomer } = useCreateOrModifyAppointmentStore(
        useShallow((state) => ({
            setMode: state.setMode,
            setAppointmentId: state.setAppointmentId,
            setNotifyCustomer: state.setNotifyCustomer,
        }))
    );
    const {
        setCreateOrModifyAppointmentDrawerOpen,
    } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            setCreateOrModifyAppointmentDrawerOpen: state.setCreateOrModifyAppointmentDrawerOpen,
        }))
    );

    const { timezone } = useUserStore(
        useShallow((state) => ({
            timezone: state.user?.timezone,
        }))
    );

    const DISPLAY_DATE_FORMAT = 'EEEE, dd. MMMM yyyy';
    const DISPLAY_TIME_FORMAT = 'HH:mm';
    const [open, setOpen] = useState(false);

    const trpcUtils = trpc.useUtils();

    // Query for fetching appointment requests
    const appointmentRequestsQuery = trpc.appointments.getIncomingAppointmentRequests.useQuery(
        {
            storeId: storeId,
        },
        {
            refetchInterval: 10000, // 10 seconds in milliseconds
        }
    );

    // Mutations for accepting and declining requests
    const acceptMutation = trpc.appointments.acceptAppointmentRequest.useMutation({
        onSuccess: () => {
            // Refetch the appointment requests after successful mutation
            appointmentRequestsQuery.refetch();
            toast.success("Appointment was accepted");
            if (calendarRef.current) {
                // calendarRef.current.getApi().refetchEvents();
                trpcUtils.appointments.getAppointmentsForScheduler.refetch();
            }
        }
    });

    const declineMutation = trpc.appointments.declineAppointmentRequest.useMutation({
        onSuccess: () => {
            // Refetch the appointment requests after successful mutation
            appointmentRequestsQuery.refetch();
            toast.success("Appointment was declined");
            if (calendarRef.current) {
                // calendarRef.current.getApi().refetchEvents();
                trpcUtils.appointments.getAppointmentsForScheduler.refetch();
            }
        }
    });

    const incomingAppointmentRequests = useMemo(() => {
        if (appointmentRequestsQuery.data?.events) {
            return appointmentRequestsQuery.data?.events;
        }
        return [];
    }, [appointmentRequestsQuery.data]);

    const numberOfRequests = useMemo(() => incomingAppointmentRequests.length, [incomingAppointmentRequests]);

    const formatDateTime = (utcDateTime: string) => {
        if (!utcDateTime) return { date: 'N/A', time: 'N/A' };
        const localDateTime = dayjs.utc(utcDateTime).tz((timezone || 'Europe/Berlin'));
        return {
            date: format(localDateTime.toDate(), DISPLAY_DATE_FORMAT, { locale: de }),
            time: localDateTime.format(DISPLAY_TIME_FORMAT),
        };
    };

    const formatDateTimeAgo = (utcDateTime: string) => {
        const now = new Date();
        const date = new Date(utcDateTime);
        const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (seconds < 60) {
            return `vor ${seconds} ${seconds === 1 ? 'Sekunde' : 'Sekunden'}`;
        }

        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) {
            return `vor ${minutes} ${minutes === 1 ? 'Minute' : 'Minuten'}`;
        }

        const hours = Math.floor(minutes / 60);
        if (hours < 24) {
            return `vor ${hours} ${hours === 1 ? 'Stunde' : 'Stunden'}`;
        }

        const days = Math.floor(hours / 24);
        if (days < 7) {
            return `vor ${days} ${days === 1 ? 'Tag' : 'Tagen'}`;
        }

        const weeks = Math.floor(days / 7);
        if (weeks < 4) {
            return `vor ${weeks} ${weeks === 1 ? 'Woche' : 'Wochen'}`;
        }

        const months = Math.floor(days / 30);
        if (months < 12) {
            return `vor ${months} ${months === 1 ? 'Monat' : 'Monaten'}`;
        }

        const years = Math.floor(days / 365);
        return `vor ${years} ${years === 1 ? 'Jahr' : 'Jahren'}`;
    };

    // Updated functions for accept/decline actions using mutations
    const handleAccept = async (appointmentId: string) => {
        if (acceptMutation.isPending) {
            return;
        }
        await acceptMutation.mutateAsync({
            appointmentId: appointmentId
        });

        // Close popover if it's the last item
        if (numberOfRequests === 1) {
            setOpen(false);
        }
    };

    const handleDecline = async (appointmentId: string) => {
        if (declineMutation.isPending) {
            return;
        }
        await declineMutation.mutateAsync({
            appointmentId: appointmentId
        });

        // Close popover if it's the last item
        if (numberOfRequests === 1) {
            setOpen(false);
        }
    };

    const handleEdit = (appointmentId: string) => {
        setMode('modify');
        setAppointmentId(appointmentId);
        setNotifyCustomer(false);
        setCreateOrModifyAppointmentDrawerOpen(true);
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant='outline' size='icon' className='relative'>
                    <CalendarIcon className='h-4 w-4' />
                    {/* Badge only shown when popover is closed and there are requests */}
                    {!open && numberOfRequests > 0 && (
                        <div className='absolute -top-2 -right-2 h-5 w-5 rounded-full bg-primary-500 flex items-center justify-center text-[10px] text-white font-medium'>
                            {numberOfRequests}
                        </div>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className='w-[450px] p-0' align="end"> {/* Remove padding here, add to inner divs */}
                <div className='flex flex-col gap-1 p-4'>
                    <h3 className="scroll-m-20 text-xl font-semibold tracking-tight">
                        Neue Terminanfragen ({numberOfRequests})
                    </h3>
                    <p className="leading-7">
                        Überprüfe und verwalte neue Terminanfragen. <br />
                        Zeitzone: {(timezone || 'Europe/Berlin').replace('_', ' ')}.
                    </p>
                </div>
                {/* Adjusted max-height and padding */}
                <div className='mt-0 max-h-[350px] overflow-y-auto px-4 pb-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 dark:scrollbar-thumb-gray-600 dark:scrollbar-track-gray-800'>
                    {appointmentRequestsQuery.isLoading ? (
                        <p className='text-sm text-muted-foreground text-center py-4'>Lade Anfragen...</p>
                    ) : numberOfRequests > 0 ? (
                        <div className='space-y-3'>
                            {incomingAppointmentRequests.map((event) => {
                                // console.log("incomingAppointmentRequest", event.start);
                                const { date: startDate, time: startTime } = formatDateTime(event.start);
                                const { time: endTime } = formatDateTime(event.end);

                                const timeSinceAppointment = formatDateTimeAgo(event.createdAt);
                                return (
                                    <div key={event.id} className='border rounded-md p-3 space-y-1 shadow-sm bg-card'>
                                        <div className='flex justify-between items-start'>
                                            <h3 className='font-semibold text-sm'>
                                                {event.metadata.firstName} {event.metadata.lastName}
                                            </h3>

                                            <span className='text-xs'>{timeSinceAppointment}</span>
                                        </div>
                                        <p className='text-xs text-muted-foreground'>
                                            <span className='font-medium text-foreground'>Behandlung:</span> {event.title}
                                        </p>
                                        <p className='text-xs text-muted-foreground'>
                                            <span className='font-medium text-foreground'>Datum:</span> {startDate}
                                        </p>
                                        <p className='text-xs text-muted-foreground'>
                                            <span className='font-medium text-foreground'>Uhrzeit:</span> {startTime} - {endTime} <span className='text-[10px]'>({(timezone || 'Europe/Berlin').replace('_', ' ')})</span>
                                        </p>
                                        <p className='text-xs text-muted-foreground truncate' title={event.metadata.customerEmail}>
                                            <span className='font-medium text-foreground'>E-Mail:</span> {event.metadata.customerEmail}
                                        </p>
                                        {event.metadata.customerPhone && (
                                            <p className='text-xs text-muted-foreground'>
                                                <span className='font-medium text-foreground'>Tel.:</span> {event.metadata.customerPhone}
                                            </p>
                                        )}
                                        {/* Add Accept/Decline buttons per request */}
                                        <div className="flex gap-2 pt-2">
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-7 px-2 text-xs flex-1 bg-red-600 hover:bg-red-700 text-white"
                                                        disabled={declineMutation.isPending && declineMutation.variables?.appointmentId === event.metadata.appointmentId}
                                                    >
                                                        Ablehnen
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Bist du dir sicher?</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            Bist du dir sicher, dass du den Termin ablehnen möchtest und auf den Status "DECLINED" setzen möchtest?
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                                        <AlertDialogAction asChild>
                                                            <Button
                                                                onClick={async () => await handleDecline(event.metadata.appointmentId)}
                                                                disabled={declineMutation.isPending && declineMutation.variables?.appointmentId === event.metadata.appointmentId}
                                                            >
                                                                {declineMutation.isPending && declineMutation.variables?.appointmentId === event.metadata.appointmentId
                                                                    ? <Loader2 className='animate-spin' />
                                                                    : null}
                                                                Ablehnen
                                                            </Button>
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                            <Button
                                                size="sm"
                                                className="h-7 px-2 text-xs flex-1 bg-green-600 hover:bg-green-700"
                                                onClick={() => handleAccept(event.metadata.appointmentId)}
                                                disabled={acceptMutation.isPending && acceptMutation.variables?.appointmentId === event.metadata.appointmentId}
                                            >
                                                {acceptMutation.isPending && acceptMutation.variables?.appointmentId === event.metadata.appointmentId
                                                    ? <Loader2 className='animate-spin' />
                                                    : null}
                                                Annehmen
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7 px-2 text-xs flex-1 text-black"
                                                onClick={() => handleEdit(event.metadata.appointmentId)}
                                            >
                                                Bearbeiten
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className='text-sm text-muted-foreground text-center py-4'>Keine neuen Terminanfragen.</p>
                    )}
                </div>
                <Button variant='outline' onClick={() => setOpen(false)} className="w-full">
                    Schließen
                </Button>
            </PopoverContent>
        </Popover>
    );
}

export const StoreSelector = () => {
    const { selectedStore, setSelectedStore } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
            setSelectedStore: state.setSelectedStore,
        }))
    );

    const { role } = useUserStore(
        useShallow((state) => ({
            role: state.user?.role,
        }))
    );

    const [resourceOrderDialogOpen, setResourceOrderDialogOpen] = useState(false);

    const { data: storesData, isLoading } = trpc.stores.getAll.useQuery({
        filters: {},
    });

    const stores = useMemo(() => {
        const data = storesData?.data || [];
        return [...data].sort((a, b) => a.name.localeCompare(b.name));
    }, [storesData?.data]);

    console.log(stores);

    useEffect(() => {
        console.log(!isLoading, stores, stores.length > 0, !selectedStore);
        if (!isLoading && stores && stores.length > 0 && (!selectedStore || !stores.some((store) => store.id === selectedStore.id))) {
            console.log("setting store to:", stores[0]);
            setSelectedStore({
                id: stores[0].id,
                label: stores[0].name,
            });
        }
    }, [stores, isLoading, selectedStore]);

    const canEditOrder = role === UserRole.ADMIN || role === UserRole.OPERATOR;

    return (
        <>
            <div className='flex items-center space-x-2'>
                <Select
                    value={selectedStore?.id.toString()}
                    onValueChange={(value) => {
                        const store = stores.find((store) => store.id.toString() === value);
                        if (store) {
                            setSelectedStore({
                                id: store.id,
                                label: store.name,
                            });
                        }
                    }}
                    disabled={isLoading}
                >
                    <SelectTrigger className='bg-white'>
                        <SelectValue placeholder='Select a store' />
                    </SelectTrigger>
                    <SelectContent>
                        {isLoading ? (
                            <SelectItem value='loading' disabled>
                                Loading stores...
                            </SelectItem>
                        ) : stores.length > 0 ? (
                            stores.map((store) => (
                                <SelectItem key={store.id} value={store.id.toString()}>
                                    {store.name}
                                </SelectItem>
                            ))
                        ) : (
                            <SelectItem value='empty' disabled>
                                No stores available
                            </SelectItem>
                        )}
                    </SelectContent>
                </Select>
                {canEditOrder && selectedStore && (
                    <Button
                        variant='outline'
                        size='icon'
                        onClick={() => setResourceOrderDialogOpen(true)}
                        title='Spaltenreihenfolge bearbeiten'
                    >
                        <Pencil className='h-4 w-4' />
                    </Button>
                )}
            </div>
            {selectedStore && (
                <ResourceOrderDialog
                    open={resourceOrderDialogOpen}
                    onOpenChange={setResourceOrderDialogOpen}
                    storeId={selectedStore.id}
                />
            )}
        </>
    );
};

type CreateOrModifyAppointmentDrawerProps = {
    calendarRef?: React.RefObject<FullCalendar | null>;
    onSubmit?: () => void;
};
export const CreateOrModifyAppointmentDrawer = ({ calendarRef, onSubmit }: CreateOrModifyAppointmentDrawerProps) => {
    const {
        selectedStore,
        createOrModifyAppointmentDrawerOpen,
        setCreateOrModifyAppointmentDrawerOpen,
        selectedDateRange,
    } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
            createOrModifyAppointmentDrawerOpen: state.createOrModifyAppointmentDrawerOpen,
            setCreateOrModifyAppointmentDrawerOpen: state.setCreateOrModifyAppointmentDrawerOpen,
            selectedDateRange: state.selectedDateRange,
        }))
    );

    const { timezone } = useUserStore(
        useShallow((state) => ({
            timezone: state.user?.timezone || 'Europe/Berlin',
        }))
    );

    const trpcUtils = trpc.useUtils();

    const { mode, setMode, appointmentId, setAppointmentId, setColor, setCustomer, setMessageFromCustomer, setMessageToCustomer, setAppointmentNotes, setStartDate, setStartTime, setSteps, setSelectedBookableItems, setColorIndex, setStatus, setNotifyCustomer, setVouchers, setType, setInfluencerType, refreshSelectableResources, reset } =
        useCreateOrModifyAppointmentStore(
            useShallow((state) => ({
                appointmentId: state.appointmentId,
                setAppointmentId: state.setAppointmentId,
                mode: state.mode,
                setMode: state.setMode,
                setColor: state.setColor,
                setCustomer: state.setCustomer,
                setMessageFromCustomer: state.setMessageFromCustomer,
                setMessageToCustomer: state.setMessageToCustomer,
                setAppointmentNotes: state.setAppointmentNotes,
                setStartDate: state.setStartDate,
                setStartTime: state.setStartTime,
                setSteps: state.setSteps,
                setSelectedBookableItems: state.setSelectedBookableItems,
                setColorIndex: state.setColorIndex,
                setStatus: state.setStatus,
                setNotifyCustomer: state.setNotifyCustomer,
                setVouchers: state.setVouchers,
                setType: state.setType,
                setInfluencerType: state.setInfluencerType,

                refreshSelectableResources: state.refreshSelectableResources,

                reset: state.reset,
            }))
        );

    const [loadingAppointment, setLoadingAppointment] = useState<boolean>(false);
    useEffect(() => {
        const fetchAppointment = async () => {
            console.log('fetchAppointment');
            if (mode === 'modify' && !!appointmentId) {
                setLoadingAppointment(true);
                const appointment = await trpcUtils.appointments.getAppointmentForModify.fetch({
                    appointmentId,
                }, {
                    gcTime: 0,
                    staleTime: 0,
                });
                if (!appointment) {
                    setLoadingAppointment(false);
                    return;
                }
                // Reset state
                // setCustomer(null);
                // setInternalNote("");
                // setMessage("");
                // setStartDate(new Date());
                // setStartTime('09:00');
                // setSteps([]);
                // setSelectedVariants([]);

                // console.log(appointment);
                const stepsOrdered = appointment.steps.sort((a, b) => new Date(a.dateFrom).getTime() - new Date(b.dateFrom).getTime());

                const firstStep = stepsOrdered[0];

                // Read date
                const storeTimezone = !!appointment.store?.timezone ? appointment.store.timezone.replace("_", "/") as IANATimezone : 'Europe/Berlin';
                const firstStepFromLocalDatetime = LocalDateTime.deserialize(firstStep.dateFromLocal!).changeTimezone(storeTimezone);
                const startTimeMinutes = firstStepFromLocalDatetime.getHours() * 60 + firstStepFromLocalDatetime.getMinutes();

                console.log('timezone')
                console.log(timezone)
                console.log('firstStepFromTimezoned')
                console.log(firstStepFromLocalDatetime.getDisplayDateTime());

                const variantSteps: Record<string, string[]> = {};
                const stepsFormatted = stepsOrdered.map((step) => {
                    // Convert UTC dates to user's timezone for each step
                    const from = LocalDateTime.deserialize(step.dateFromLocal!).changeTimezone(storeTimezone);
                    const to = LocalDateTime.deserialize(step.dateUntilLocal!).changeTimezone(storeTimezone);

                    // Calculate minuteStart and minuteEnd relative to startTimeTimezoned
                    const fromMinutes = from.getHours() * 60 + from.getMinutes();
                    const toMinutes = to.getHours() * 60 + to.getMinutes();
                    const minuteStart = fromMinutes - startTimeMinutes;
                    const minuteEnd = toMinutes - startTimeMinutes;

                    const variant = step.originalTreatmentStep?.variant;
                    if (variant?.id && variant.id in variantSteps) {
                        variantSteps[variant.id].push(step.id);
                    } else if (variant?.id) {
                        variantSteps[variant.id] = [step.id];
                    }

                    const uniquePractitionerCategories: {
                        id: number;
                        name: string;
                    }[] = [];
                    step.practitioners.forEach((assoc) => {
                        const category = assoc.practitioner.practitioner_category;
                        if (category === null || uniquePractitionerCategories.some((cat) => cat.id === category.id)) {
                            return;
                        }
                        uniquePractitionerCategories.push(category);
                    })

                    const uniqueRoomCategories: {
                        id: number;
                        name: string;
                    }[] = [];
                    step.rooms.forEach((assoc) => {
                        const category = assoc.room.room_category;
                        if (category === null || uniqueRoomCategories.some((cat) => cat.id === category.id)) {
                            return;
                        }
                        uniqueRoomCategories.push(category);
                    })

                    const uniqueEquipmentCategories: {
                        id: number;
                        name: string;
                    }[] = [];
                    step.equipment.forEach((assoc) => {
                        const category = assoc.equipment.equipment_category;
                        if (category === null || uniqueEquipmentCategories.some((cat) => cat.id === category.id)) {
                            return;
                        }
                        uniqueEquipmentCategories.push(category);
                    })

                    console.log(step);

                    const practitioners = step.practitioners?.map((association) => ({
                        id: association.practitioner.id,
                        name: `${association.practitioner.firstName} ${association.practitioner.lastName}`,
                        categoryId: association.practitioner.practitioner_category!.id,
                        categoryName: association.practitioner.practitioner_category!.name,
                    })) || [];
                    const rooms = step.rooms?.map((association) => ({
                        id: association.room.id,
                        name: association.room.name,
                        categoryId: association.room.room_category!.id,
                        categoryName: association.room.room_category!.name,
                    })) || []
                    const equipment = step.equipment?.map((association) => ({
                        id: association.equipment.id,
                        name: association.equipment.name,
                        categoryId: association.equipment.equipment_category!.id,
                        categoryName: association.equipment.equipment_category!.name,
                    })) || []

                    return {
                        id: step.id,
                        name: step.title,
                        shortDescription: step.description && step.description.length > 0 ? step.description : (step.originalTreatmentStep?.shortDescription || ''),
                        minuteStart: minuteStart,
                        minuteEnd: minuteEnd,
                        treatmentColor: appointment.color,
                        treatmentVariantId: step.originalTreatmentStep?.variantId || null,
                        originalTreatmentStepId: step.originalTreatmentStepId,
                        bookableItemId: (step as any).bookableItemId || null, // Will be populated from DB
                        selectablePractitioners: [],
                        selectableRooms: [],
                        selectableEquipment: [],
                        practitioners: practitioners,
                        rooms: rooms.map((room) => ({
                            ...room,
                            equipment: [] as any,
                        })),
                        equipment: equipment,
                        initialPractitioners: practitioners,
                        initialRooms: rooms.map((room) => ({
                            ...room,
                            equipment: [] as any,
                        })),
                        initialEquipment: equipment,
                        acceptablePractitioners: !step.originalTreatmentStep ? uniquePractitionerCategories.map((category) => ({
                            categoryId: category.id,
                        })) : step.originalTreatmentStep.practitionerCategoryConstraints.map((constraint) => ({
                            categoryId: constraint.practitionerCategory.id,
                        })),
                        acceptableRooms: !step.originalTreatmentStep ? uniqueRoomCategories.map((category) => ({
                            categoryId: category.id,
                        })) : step.originalTreatmentStep.roomCategoryConstraints.map((constraint) => ({
                            categoryId: constraint.roomCategory.id,
                        })),
                        acceptableEquipments: !step.originalTreatmentStep ? uniqueEquipmentCategories.map((category) => ({
                            categoryId: category.id,
                        })) : step.originalTreatmentStep.equipmentCategoryConstraints.map((constraint) => ({
                            categoryId: constraint.equipmentCategory.id,
                        })),
                    };
                });
                const startTime = `${firstStepFromLocalDatetime.getHours().toString().padStart(2, '0')}:${firstStepFromLocalDatetime.getMinutes().toString().padStart(2, '0')}`;
                
                // Fetch cart data to get bookable items
                const cartData = await trpcUtils.appointments.getAppointmentCartForModify.fetch({
                    appointmentId,
                }, {
                    gcTime: 0,
                    staleTime: 0,
                });

                // Build selectedBookableItems from cart data (bookable_item type)
                const bookableItemsFromCart = (cartData?.items || []).map((item, idx) => {
                    const color = TREATMENT_COLORS[idx % TREATMENT_COLORS.length];
                    const stepIds = variantSteps[item.treatmentVariantId] || [];
                    return {
                        type: 'bookable_item' as const,
                        bookableItemId: item.bookableItemId,
                        bookableItemType: item.bookableItemType,
                        bookableItemName: item.bookableItemName,
                        color: color,
                        treatmentVariantId: item.treatmentVariantId,
                        treatmentVariantName: item.treatmentVariantName,
                        priceCents: item.priceCents,
                        bundleId: item.bundleId,
                        bundleName: item.bundleName,
                        bundleUnitQuantity: item.bundleUnitQuantity,
                        bundleUnitPriceGross: item.bundleUnitPriceGross,
                        isBundleRedemption: item.isBundleRedemption,
                        addOns: item.addOns,
                        stepIds: stepIds,
                    };
                });

                // Determine which variant IDs are already covered by bookable items from cart
                const coveredVariantIds = new Set(
                    bookableItemsFromCart.flatMap(bi => [
                        bi.treatmentVariantId,
                        ...bi.addOns.map(addon => addon.variantId),
                    ])
                );

                // For variants NOT covered by cart bookable items, create legacy items
                // This handles old appointments that have no bookable item data in the cart
                const legacyItems = (appointment.originalTreatmentVariants || [])
                    .filter(otv => !coveredVariantIds.has(otv.treatmentVariant.id))
                    .map((otv, idx) => {
                        const color = TREATMENT_COLORS[(bookableItemsFromCart.length + idx) % TREATMENT_COLORS.length];
                        const stepIds = variantSteps[otv.treatmentVariant.id] || [];
                        return {
                            type: 'legacy' as const,
                            bookableItemId: uuidv4(),
                            bookableItemType: 'SERVICE' as const,
                            bookableItemName: otv.treatmentVariant.name,
                            color,
                            treatmentVariantId: otv.treatmentVariant.id,
                            treatmentVariantName: otv.treatmentVariant.name,
                            priceCents: otv.treatmentVariant.priceCents ?? 0,
                            addOns: [],
                            stepIds,
                        };
                    });

                const selectedBookableItems = [...bookableItemsFromCart, ...legacyItems];

                // Update steps with bookableItemId based on variant mapping
                const stepsWithBookableItems = stepsFormatted.map((step) => {
                    const bookableItem = selectedBookableItems.find(
                        (bi) => bi.treatmentVariantId === step.treatmentVariantId
                    );
                    return {
                        ...step,
                        bookableItemId: bookableItem?.bookableItemId || null,
                        treatmentColor: bookableItem?.color || step.treatmentColor,
                    };
                });

                setCustomer(appointment.customer);
                setColor(appointment.color || '#bdd7a5');
                setMessageFromCustomer(appointment?.messageFromCustomer || '');
                setMessageToCustomer(appointment?.messageToCustomer || '');
                setAppointmentNotes(appointment?.appointmentNotes || '');
                setVouchers(appointment?.couponCodes || '');
                setStartDate(firstStepFromLocalDatetime);
                setStartTime(startTime);
                setSteps(stepsWithBookableItems);
                setSelectedBookableItems(selectedBookableItems);
                setColorIndex(selectedBookableItems.length);
                setStatus(appointment.status);
                setType((appointment as any).type ?? 'NORMAL');
                setInfluencerType(appointment.influencerType ?? null);
                setNotifyCustomer(appointment.status === AppointmentStatus.REQUESTED);
                setLoadingAppointment(false);

                if (!!selectedStore?.id) {
                    refreshSelectableResources(selectedStore.id, timezone, trpcUtils);
                }
            }
        };

        if (mode === 'modify' && !!appointmentId) {
            fetchAppointment();
        }
    }, [appointmentId, timezone]);

    useEffect(() => {
        if (createOrModifyAppointmentDrawerOpen === true && mode === 'create') {
            const startDate = selectedDateRange.startDate;
            setStartDate(startDate);
        }

        if (createOrModifyAppointmentDrawerOpen === false) {
            setAppointmentId(null);
        }
    }, [createOrModifyAppointmentDrawerOpen]);

    return (
        <Sheet open={createOrModifyAppointmentDrawerOpen} onOpenChange={setCreateOrModifyAppointmentDrawerOpen}>
            <SheetContent hideOverlay className='sm:max-w-md md:max-w-lg lg:max-w-3xl overflow-y-auto'>
                <SheetHeader>
                    <SheetTitle>{mode === 'create' ? 'Termin erstellen' : 'Termin bearbeiten'}</SheetTitle>
                    <SheetDescription className='flex flex-col gap-2'>
                        <span>Fülle die Felder aus um einen Termin zu {mode === 'create' ? 'erstellen' : 'bearbeiten'}</span>
                    </SheetDescription>
                </SheetHeader>
                <div className='p-4'>
                    {loadingAppointment ? (
                        <Loader2 className='animate-spin' />
                    ) : (
                        <>
                            <div className="mt-4">
                                <CreateOrModifyAppointmentForm
                                    onCancel={() => {
                                        setCreateOrModifyAppointmentDrawerOpen(false);
                                    }}
                                    onSubmit={() => {
                                        setCreateOrModifyAppointmentDrawerOpen(false);
                                        trpcUtils.appointments.getAppointmentsForScheduler.refetch();
                                        toast.success("Termin gespeichert");
                                        if (onSubmit) {
                                            onSubmit();
                                        }
                                    }}
                                />
                            </div>
                        </>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};

const CreateAppointmentButton = () => {
    const { setCreateOrModifyAppointmentDrawerOpen } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            setCreateOrModifyAppointmentDrawerOpen: state.setCreateOrModifyAppointmentDrawerOpen,
        }))
    );

    const { reset } = useCreateOrModifyAppointmentStore(
        useShallow((state) => ({
            reset: state.reset
        }))
    );

    const handleClick = () => {
        reset();
        setCreateOrModifyAppointmentDrawerOpen(true);
    }

    return (
        <Button variant='outline' onClick={() => handleClick()}>
            <Plus className='h-4 w-4 mr-2' />
            Termin erstellen
        </Button>
    );
};

type ScheduleEditorModalProps = {
    calendarRef: React.RefObject<FullCalendar | null>;
}
const ScheduleEditorModal = ({ calendarRef }: ScheduleEditorModalProps) => {
    const { open, scheduleId, type, clickedDateRange, setOpen, reset } = useScheduleEditorModalStore(
        useShallow((state) => ({
            open: state.open,
            scheduleId: state.scheduleId,
            type: state.type,
            clickedDateRange: state.clickedDateRange,
            setOpen: state.setOpen,
            setScheduleId: state.setScheduleId,
            setType: state.setType,
            reset: state.reset,
        }))
    );

    console.log("ScheduleEditorModal");

    const trpcUtils = trpc.useUtils();

    const { timezone: userTimezone } = useUserStore(
        useShallow((state) => ({ timezone: state.user?.timezone }))
    );

    const practitionerScheduleEntryQuery = trpc.practitioners.getScheduleEntry.useQuery({
        id: scheduleId || '',
    }, {
        enabled: scheduleId !== null && type === 'practitioner'
    });

    const equipmentScheduleEntryQuery = trpc.equipment.getScheduleEntry.useQuery({
        id: scheduleId || '',
    }, {
        enabled: scheduleId !== null && type === 'equipment'
    });

    const scheduleEntry = useMemo(() => {
        if (type === 'practitioner') {
            return practitionerScheduleEntryQuery?.data || null;
        } else if (type === 'equipment') {
            return equipmentScheduleEntryQuery?.data || null;
        }
        return null;
    }, [type, practitionerScheduleEntryQuery, equipmentScheduleEntryQuery]);
    const dataObjectId = useMemo(() => {
        if (scheduleEntry) {
            if ('practitionerId' in scheduleEntry) {
                return scheduleEntry.practitionerId;
            } else if ('equipmentId' in scheduleEntry) {
                return scheduleEntry.equipmentId;
            }
        }
    }, [scheduleEntry]);

    const { timezone } = useUserStore(
        useShallow((state) => ({
            timezone: state.user?.timezone,
        }))
    );

    const { selectedStore } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
        }))
    );

    useEffect(() => {
        if (!open) {
            reset();
        }
    }, [open]);

    if (!open) {
        return null;
    }

    return (
        <Dialog
            open={open}
            onOpenChange={(open) => {
                if (open === false) {
                    reset();
                }
                setOpen(open);
            }}
        >
            <DialogContent className='max-w-xl max-h-full overflow-y-scroll'>
                <DialogHeader>
                    <DialogTitle>Blocker bearbeiten</DialogTitle>
                </DialogHeader>

                <div className='space-y-4'>
                    {selectedStore && dataObjectId && type && scheduleEntry ? (
                        <ScheduleEntryForm
                            key={scheduleEntry.id ?? 'new'} // Force re-render on edit vs add
                            dataObjectId={dataObjectId}
                            dataObjectType={type}
                            overwriteEntryType='BLOCKED'
                            storeId={selectedStore.id}
                            userTimezone={userTimezone || 'Europe/Berlin'}
                            scheduleEntryId={scheduleEntry?.id ?? undefined}
                            selectedSlot={clickedDateRange ?? undefined}
                            onSuccess={() => {
                                setOpen(false);
                                reset();
                                trpcUtils.practitioners.getScheduleEntry.invalidate();
                                trpcUtils.equipment.getScheduleEntry.invalidate();
                                if (calendarRef?.current) {
                                    calendarRef.current.getApi().refetchEvents();
                                }
                            }}
                            onCancel={() => {
                                setOpen(false);
                                reset();
                            }}
                        />
                    ) : (
                        <div className='w-[200px] h-[200px] flex items-center justify-center'>
                            <Loader2 className='animate-spin' />
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

type Resource = {
    id: string;
    title: string;
    storeId: number | null;
    storeIds: number[] | null;
    order: number;
};

type ResourceCalendarProps = {
    resources: Resource[];
};

// Helper function to determine the view based on duration
const getViewFromDateRange = (startDate: LocalDateTime, endDate: LocalDateTime): string => {
    const durationInDays = Math.ceil((endDate.valueOf() - startDate.valueOf()) / (1000 * 60 * 60 * 24)) + 1;
    if (durationInDays === 1) {
        return 'resourceTimeGridDay';
    } else if (durationInDays <= 3) {
        return 'timeGridThreeDays';
    } else if (durationInDays <= 7) {
        // Assuming 'timeGridWeek' is the intended view for up to 7 days
        // If you have a specific 'resourceTimeGridWeek', use that.
        return 'timeGridWeek';
    } else {
        return 'dayGridMonth';
    }
};


const ResourceCalendar = ({ resources }: ResourceCalendarProps) => {
    const { timezone } = useUserStore(
        useShallow((state) => ({
            timezone: state.user?.timezone,
        }))
    );

    const { selectedStore, viewMode, selectedDateRange, appointmentFilters, roomFilters, equipmentFilters, practitionerFilters } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
            viewMode: state.viewMode, // Keep track of the desired base view mode
            selectedDateRange: state.selectedDateRange,
            appointmentFilters: state.appointmentFilters,
            roomFilters: state.roomFilters,
            equipmentFilters: state.equipmentFilters,
            practitionerFilters: state.practitionerFilters,
        }))
    );

    const trpcUtils = trpc.useUtils();
    const calendarRef = useRef<FullCalendar>(null);
    const [loading, setLoading] = useState<boolean>(false);

    // --- Filtered Resources ---
    const filteredResources = useMemo(() => {
        if (!resources || !selectedStore) {
            return [];
        }

        const currentStoreId = selectedStore.id;
        const storeResources = resources.filter((resource) => !resource.storeIds || resource.storeIds.includes(currentStoreId));

        const allFiltersEmpty = roomFilters.size === 0 && equipmentFilters.size === 0 && practitionerFilters.size === 0;

        if (allFiltersEmpty) {
            return storeResources;
        }

        // Filter resources based on their type and ID AND selectedStore
        const filtered = storeResources.filter((resource) => {
            const resourceId = resource.id;

            if (resourceId.startsWith('practitioner-')) {
                return practitionerFilters.has(resourceId.replace('practitioner-', ''));
            } else if (resourceId.startsWith("unassigned") || resourceId.startsWith("waiting")) {
                return practitionerFilters.has(resourceId);
            }
            if (resourceId.startsWith('room-')) {
                return roomFilters.has(resourceId.replace('room-', ''));
            }
            if (resourceId.startsWith('equipment-')) {
                return equipmentFilters.has(resourceId.replace('equipment-', ''));
            }
            return false; // If filters are active, only show filtered items
        });

        // Sorting logic might need revisiting (e.g., fetch event counts separately if needed)
        return filtered;

    }, [resources, selectedStore, roomFilters, equipmentFilters, practitionerFilters]);

    useEffect(() => {
        if (calendarRef.current) {
            trpcUtils.appointments.getAppointmentsForScheduler.refetch();
            calendarRef.current.getApi().refetchEvents();
        }
    }, [filteredResources, appointmentFilters]);

    const [dateRangeForFetching, setDateRangeForFetching] = useState<DateRange>(selectedDateRange);
    useEffect(() => {
        setDateRangeForFetching({
            startDate: selectedDateRange.startDate,
            endDate: selectedDateRange.endDate,
        });
    }, [selectedDateRange]);

    console.log("dateRangeForFetching");
    console.log(dateRangeForFetching);

    const queryFilters = useMemo(() => {
        return {
            storeId: selectedStore?.id || 0,
            dateRange: {
                startDate: dateRangeForFetching.startDate.toJson(),
                endDate: dateRangeForFetching.endDate.addDays(1).toJson(),
            },
            appointmentFilters: { ...appointmentFilters, status: Array.from(appointmentFilters.status) },
            practitionerFilters: practitionerFilters.size === 0 ? undefined : Array.from(practitionerFilters), // Example assuming Set<string>
            roomFilters: roomFilters.size === 0 ? undefined : Array.from(roomFilters),
            equipmentFilters: equipmentFilters.size === 0 ? undefined : Array.from(equipmentFilters),
            mode: (viewMode === 'resourceTimeGridDay' ? 'steps' : 'appointments') as 'steps' | 'appointments',
        };
    }, [
        selectedStore,
        dateRangeForFetching,
        appointmentFilters,
        practitionerFilters,
        roomFilters,
        equipmentFilters,
        viewMode
    ]);

    const { data: appointmentsData, refetch: refetchAppointments } = trpc.appointments.getAppointmentsForScheduler.useQuery(
        queryFilters,
        {
            refetchInterval: 15 * 1000, // milliseconds
            staleTime: 15 * 1000, // milliseconds
            placeholderData: keepPreviousData,
            enabled: !!selectedStore?.id && selectedStore.id !== 0,
        }
    );

    useEffect(() => {
        refetchAppointments();
    }, [queryFilters]);

    const handleFetchEvents = useCallback((arg: { start: Date; end: Date }, successCallback: (events: any[]) => void, failureCallback: (error: Error) => void) => {
        console.log("handleFetchEvents");
        const events = (appointmentsData?.events || []).map((event) => {
            let startDate: Date | null = null;
            let endDate: Date | null = null;
            try {
                startDate = LocalDateTime.deserialize(event.startLocalSerialized).toUTC();
                endDate = LocalDateTime.deserialize(event.endLocalSerialized).toUTC();
            } catch (e) {
                console.log("failed to parse date of event:");
                console.log(e);
                // startDate = new Date(event.start);
                // endDate = new Date(event.end);
            }

            return {
                ...event,
                start: startDate,
                end: endDate,
            }
        });

        if (viewMode !== 'resourceTimeGridDay') {
            // Filter away schedule/shift background events (availabilities/blocked times for resources) 
            // unless we're on resourceTimeGridDay
            const filteredEvents = events.filter((event) => event.metadata?.type !== 'AVAILABLE').map((event) => ({ ...event, display: undefined })).filter((event) => {
                const resourceIds = event.resourceIds as string[];
                const allowedResourceTypes = new Set<string>();
                filteredResources.forEach((r) => {
                    if (r.id === 'unassigned' || r.id === 'waiting' || r.id === 'reception') {
                        allowedResourceTypes.add(r.id);
                    } else {
                        const type = r.id.split("-")[0];
                        allowedResourceTypes.add(type);
                    }
                });
                let anyResourceTypePassed = false;
                for (const allowedResourceType of allowedResourceTypes) {
                    const isResourceTypeFiltered = filteredResources.some((r) => {
                        if (r.id === 'unassigned' || r.id === 'waiting' || r.id === 'reception') {
                            return r.id === allowedResourceType;
                        } else {
                            return r.id.split("-")[0] === allowedResourceType
                        }
                    });
                    if (!isResourceTypeFiltered) {
                        continue;
                    }
                    const resourceIdsOfType = resourceIds.filter((r) => {
                        if (r === 'unassigned' || r === 'waiting' || r === 'reception') {
                            return r === allowedResourceType;
                        } else {
                            return r.split("-")[0] === allowedResourceType
                        }
                    });
                    const filteredResourcesOfType = filteredResources.filter((r) => {
                        if (r.id === 'unassigned' || r.id === 'waiting' || r.id === 'reception') {
                            return r.id === allowedResourceType;
                        } else {
                            return r.id.split("-")[0] === allowedResourceType
                        }
                    });

                    // console.log(filteredResourcesOfType);
                    // any filter has to match
                    const isFilteredResourceAllocated = filteredResourcesOfType.some((r) => resourceIdsOfType.includes(r.id));
                    if (!isFilteredResourceAllocated) {
                        continue
                    } else {
                        anyResourceTypePassed = true
                    }
                }
                return anyResourceTypePassed;
            })
            successCallback(filteredEvents);
        } else {
            successCallback(events);
        }
    }, [appointmentsData, viewMode]);


    useEffect(() => {
        if (calendarRef.current && selectedDateRange) {
            const calendarApi = calendarRef.current.getApi();
            const currentView = calendarApi.view;

            const needsDateChange = selectedDateRange.startDate.valueOf() !== currentView.currentStart.valueOf();
            const targetView = getViewFromDateRange(selectedDateRange.startDate, selectedDateRange.endDate);
            const needsViewChange = currentView.type !== targetView;

            if (needsDateChange || needsViewChange) {
                queueMicrotask(() => {
                    const currentApi = calendarRef.current?.getApi();
                    if (!currentApi) {
                        console.log("Calendar API became unavailable before microtask execution.");
                        return;
                    }

                    try {
                        if (needsDateChange) {
                            console.log('(Microtask) Navigating to date:', selectedDateRange.startDate);
                            currentApi.gotoDate(selectedDateRange.startDate.getDisplayDate());
                        }

                        if (needsViewChange) {
                            console.log(`(Microtask) Changing view from ${currentView.type} to ${targetView}`);
                            currentApi.changeView(targetView);
                        }
                    } catch (error) {
                        console.error("Error executing deferred FullCalendar API calls:", error);
                    }
                });
            } else {
                console.log('Date and view already match target state, skipping deferred update.');
            }
        }
    }, [selectedDateRange]);


    const calculatedInitialView = useMemo(() => {
        // Use the viewMode from store as fallback or primary if calculation isn't desired initially
        return viewMode || getViewFromDateRange(selectedDateRange.startDate, selectedDateRange.endDate);
    }, [selectedDateRange, viewMode]);


    const { slotMinTime, slotMaxTime } = useMemo(() => {
        const targetTimezone = 'Europe/Berlin';
        const targetTimezoneOffset = dayjs.tz(new Date(), targetTimezone).utcOffset() / 60; // 1 or 2.
        const targetMinHour = 7; // 07:00
        const targetMaxHour = 21; // 21:00

        const actualTimezone = timezone || 'Europe/Berlin';
        const actualTimezoneUtcOffset = dayjs.tz(new Date(), actualTimezone).utcOffset() / 60; // Example -6 if EST timezone
        const actualTimezoneTargetOffset = actualTimezoneUtcOffset - targetTimezoneOffset;

        const slotMinTimeHr = parseInt((targetMinHour + actualTimezoneTargetOffset).toFixed(0));
        const slotMaxTimeHr = parseInt((targetMaxHour + actualTimezoneTargetOffset).toFixed(0));

        return {
            slotMinTime: `${slotMinTimeHr >= 10 ? slotMinTimeHr : `0${slotMinTimeHr}`}:00`,
            slotMaxTime: `${slotMaxTimeHr >= 10 ? slotMaxTimeHr : `0${slotMaxTimeHr}`}:00`,
        }
    }, [timezone]);

    // Set up auto-refresh of calendar events every 10 seconds
    // useEffect(() => {
    //     // Only set up the interval if the calendar reference exists
    //     if (!calendarRef.current) return;

    //     const intervalId = setInterval(() => {
    //         if (calendarRef.current) {
    //             console.log('Auto-refreshing calendar events');
    //             calendarRef.current.getApi().refetchEvents();
    //         }
    //     }, 30000); // 30 seconds in milliseconds

    //     // Clean up the interval when the component unmounts
    //     return () => {
    //         clearInterval(intervalId);
    //     };
    // }, [calendarRef]);

    console.log(slotMinTime, slotMaxTime);

    console.log("selectedDateRange.startDate");
    console.log(selectedDateRange.startDate);


    return (
        <Fragment>
            {/* <MoveAppointmentModal calendarRef={calendarRef} /> */}
            <ScheduleEditorModal calendarRef={calendarRef} />
            {/* <CreateOrModifyAppointmentDrawer calendarRef={calendarRef} /> */}

            <div className='relative flex flex-col gap-4'>
                {/* Loading Overlay */}
                {loading && (
                    <div className='absolute w-full h-full bg-black/20 z-50 flex items-center justify-center pointer-events-none transition-opacity duration-200 opacity-100'>
                        <Loader2 className='w-8 h-8 text-white animate-spin' />
                    </div>
                )}

                {/* --- Responsive Header Section --- */}
                <div className='flex flex-col gap-2'>
                    <div className='flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2'>
                        <StoreSelector />
                        <div className='flex flex-row gap-2'>
                            <CreateAppointmentButton />
                            <CalendarScheduleTimeBlockEntry
                                calendarRef={calendarRef}
                                userTimezone={timezone || 'Europe/Berlin'}
                                triggerButtonLabel='Neuer Blocker'
                            />
                        </div>
                    </div>
                    <div className='flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2'>
                        <div className='flex flex-row flex-wrap items-center gap-2'>
                            <TodayButton />
                            <CalendarDatePickerWithRange />
                            <CalendarResourcesFilter />
                            {selectedStore && calendarRef.current && (
                                <IncomingAppointmentRequestsWidget storeId={selectedStore.id} calendarRef={calendarRef} />
                            )}
                        </div>
                        <div className='flex flex-row flex-wrap items-center justify-end gap-2'>
                            {calendarRef.current && <ViewModeSelector calendarRef={calendarRef} />}
                        </div>
                    </div>
                </div>

                {/* --- FullCalendar Component --- */}
                <FullCalendar
                    schedulerLicenseKey='0862586236-fcs-1713283190'
                    timeZone={timezone || 'Europe/Berlin'}
                    stickyFooterScrollbar
                    height='auto'
                    contentHeight="auto"
                    aspectRatio={2.0}
                    ref={calendarRef}
                    dayMinWidth={180}
                    plugins={[
                        timeGridPlugin,
                        dayGridPlugin,
                        listPlugin,
                        // interactionPlugin,
                        resourceTimeGridPlugin,
                        resourceTimelinePlugin,
                        dayjsTimeZonePlugin,
                        scrollGridPlugin,
                    ]}
                    locale='de'
                    firstDay={1}
                    nowIndicator={true}
                    initialView={calculatedInitialView}
                    initialDate={selectedDateRange.startDate.getDisplayDate()}
                    headerToolbar={false}
                    eventOverlap={true}
                    titleFormat={{ year: 'numeric', month: 'long', day: 'numeric' }}
                    buttonText={{
                        today: 'Heute',
                        month: 'Monat',
                        week: 'Woche',
                        day: 'Tag',
                        resourceTimeGridDay: 'Tag',
                        listWeek: 'Liste',
                        timeGridThreeDays: '3 Tage',
                    }}
                    views={{
                        timeGridThreeDays: { type: 'timeGrid', duration: { days: 3 } },
                    }}
                    slotMinTime={slotMinTime}
                    slotMaxTime={slotMaxTime}
                    eventMinHeight={10}
                    slotDuration='00:15:00'
                    slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
                    events={handleFetchEvents}
                    eventContent={(eventContent: EventContentArg) => <EventPopover info={eventContent} />}
                    // eventDrop={(eventDropArg: EventDropArg) => {
                    //     const moveAppointmentStore = useMoveAppointmentModalStore.getState();
                    //     moveAppointmentStore.setEventDropArg(eventDropArg);
                    //     moveAppointmentStore.setOpen(true);
                    // }}
                    droppable={false}
                    eventResizableFromStart
                    dragRevertDuration={200}
                    resources={filteredResources}
                    resourceOrder='order'
                    datesAboveResources
                    selectable={true}
                    lazyFetching={true}
                    viewClassNames={['border-0', 'text-sm']}
                    dayHeaderClassNames={['border-0', 'text-sm', 'text-black']}
                    resourceLabelClassNames={['text-black']}
                    dayCellClassNames={['border-0', 'text-sm']}
                    eventClassNames={['border-0', 'text-sm']}
                    slotLabelClassNames={['border-0']}
                    nowIndicatorClassNames={['z-50']}
                    // slotLaneClassNames={[
                    //     'border', 'border-gray-100', 'bg-white', 'text-sm', 'cursor-pointer',
                    //     'hover:bg-gray-100',
                    // ]}
                    allDayClassNames={['border-0', 'text-sm']}
                    allDayText='Ganztägig'
                    allDaySlot={false}
                    businessHours={{
                        daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
                        startTime: slotMinTime,
                        endTime: slotMaxTime,
                    }}
                    slotEventOverlap={false}
                />
            </div>
        </Fragment>
    );
};

const AppointmentsCalendar = () => {
    const { setAllPractitioners, setAllRooms, setAllEquipment, selectedStore } = useAppointmentsCalendarStore(useShallow((state) => ({
        setAllPractitioners: state.setAllPractitioners,
        setAllRooms: state.setAllRooms,
        setAllEquipment: state.setAllEquipment,
        selectedStore: state.selectedStore,
    })))

    // Fetch resources with less frequent updates (every 5 minutes)
    const {
        data: resourcesData,
        isLoading: isLoadingResources,
        refetch: refetchResources,
    } = trpc.appointments.getResources.useQuery(undefined, {
        refetchInterval: 300000, // 5 minutes
        refetchOnWindowFocus: true,
        staleTime: 300000,
    });

    // Fetch calendar config for the selected store
    const { data: configData } = trpc.appointmentsCalendarConfig.getByStoreId.useQuery(
        { storeId: selectedStore?.id || 0 },
        {
            enabled: !!selectedStore?.id,
            refetchOnWindowFocus: false,
        }
    );

    // Format resources for the calendar
    const resources = useMemo(() => {
        if (!resourcesData) {
            return [];
        }

        const config = configData?.data;
        const configResources = config?.resources || [];
        const hasConfig = configResources.length > 0;

        // Build a map of all available resources by their IDs
        const resourceMap = new Map<string, any>();

        // Add special columns
        resourceMap.set('unassigned', {
            id: 'unassigned',
            title: 'Nicht zugewiesen',
            storeId: null,
            storeIds: null,
            model: 'unassigned',
            modelEntryId: null,
        });
        resourceMap.set('waiting', {
            id: 'waiting',
            title: 'Einwirkzeit / Durchlaufzeit',
            storeId: null,
            storeIds: null,
            model: 'waiting',
            modelEntryId: null,
        });
        resourceMap.set('reception', {
            id: 'reception',
            title: 'Empfang',
            storeId: null,
            storeIds: null,
            model: 'reception',
            modelEntryId: null,
        });

        // Add practitioners
        (resourcesData.practitioners || []).forEach((practitioner) => {
            resourceMap.set(`practitioner-${practitioner.id}`, {
                id: `practitioner-${practitioner.id}`,
                title: `${practitioner.firstName} ${practitioner.lastName}`,
                storeId: practitioner.storeId,
                storeIds: practitioner.practitioner_store_practitioner_store_practitionerTopractitioner.map((assoc) => assoc.store_practitioner_store_storeTostore.id),
                eventCount: practitioner._count.appointmentStepAssociations,
                model: 'practitioners',
                modelEntryId: practitioner.id,
            });
        });

        // Add rooms
        (resourcesData.rooms || []).forEach((room) => {
            resourceMap.set(`room-${room.id}`, {
                id: `room-${room.id}`,
                title: room.name,
                storeId: room.storeId,
                storeIds: [room.storeId],
                eventCount: room._count.appointmentStepAssociations,
                model: 'rooms',
                modelEntryId: room.id,
            });
        });

        // Add equipment
        (resourcesData.equipment || []).forEach((equipment) => {
            resourceMap.set(`equipment-${equipment.id}`, {
                id: `equipment-${equipment.id}`,
                title: equipment.name,
                storeId: equipment.storeId,
                storeIds: [equipment.storeId],
                eventCount: equipment._count.appointmentStepAssociations,
                model: 'equipment',
                modelEntryId: equipment.id,
            });
        });

        if (hasConfig) {
            // Use config order
            const formattedResources: any[] = [];
            const addedResourceIds = new Set<string>();
            
            configResources.forEach((configRes) => {
                let resourceId = '';
                
                switch (configRes.type) {
                    case 'NOT_ASSIGNED':
                        resourceId = 'unassigned';
                        break;
                    case 'PROCESSING_TIME':
                        resourceId = 'waiting';
                        break;
                    case 'RECEPTION':
                        resourceId = 'reception';
                        break;
                    case 'PRACTITIONER':
                        if (configRes.practitioner) {
                            resourceId = `practitioner-${configRes.practitioner.id}`;
                        }
                        break;
                    case 'ROOM':
                        if (configRes.room) {
                            resourceId = `room-${configRes.room.id}`;
                        }
                        break;
                    case 'DEVICE':
                        if (configRes.device) {
                            resourceId = `equipment-${configRes.device.id}`;
                        }
                        break;
                }

                if (resourceId && resourceMap.has(resourceId)) {
                    const resource = resourceMap.get(resourceId);
                    // Only include resources that belong to the selected store
                    if (!selectedStore || 
                        !resource.storeIds || 
                        resource.storeIds.includes(selectedStore.id) ||
                        resource.storeId === selectedStore.id ||
                        !resource.storeId) {
                        formattedResources.push({
                            ...resource,
                            order: configRes.orderIdx,
                        });
                        addedResourceIds.add(resourceId);
                    }
                }
            });

            // Back-fill any resources that exist in resourceMap but weren't in the config
            // These go at the end of the list
            const maxOrder = formattedResources.length > 0 
                ? Math.max(...formattedResources.map(r => r.order)) 
                : 0;
            let backfillOrder = maxOrder + 1;

            resourceMap.forEach((resource, resourceId) => {
                if (!addedResourceIds.has(resourceId)) {
                    // Only include resources that belong to the selected store
                    if (!selectedStore || 
                        !resource.storeIds || 
                        resource.storeIds.includes(selectedStore.id) ||
                        resource.storeId === selectedStore.id ||
                        !resource.storeId) {
                        formattedResources.push({
                            ...resource,
                            order: backfillOrder++,
                        });
                    }
                }
            });

            return formattedResources;
        } else {
            // Fall back to alphabetical sorting
            // Sort practitioners alphabetically
            const sortedPractitioners = [...(resourcesData.practitioners || [])].sort((a, b) => {
                const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
                const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
                return nameA.localeCompare(nameB);
            });
        
            // Sort rooms alphabetically
            const sortedRooms = [...(resourcesData.rooms || [])].sort((a, b) => {
                return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
            });
        
            // Sort equipment alphabetically
            const sortedEquipment = [...(resourcesData.equipment || [])].sort((a, b) => {
                return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
            });
        
            const formattedResources = [
                // Unassigned
                {
                    id: 'unassigned',
                    title: 'Nicht zugewiesen',
                    storeId: null,
                    storeIds: null,
                    order: -3,
                    model: 'unassigned',
                    modelEntryId: null,
                },
                {
                    id: 'waiting',
                    title: 'Einwirkzeit / Durchlaufzeit',
                    storeId: null,
                    storeIds: null,
                    order: -2,
                    model: 'waiting',
                    modelEntryId: null,
                },
                {
                    id: 'reception',
                    title: 'Empfang',
                    storeId: null,
                    storeIds: null,
                    order: -1,
                    model: 'reception',
                    modelEntryId: null,
                },
                // Practitioners (alphabetically sorted)
                ...sortedPractitioners.map((practitioner, idx) => ({
                    id: `practitioner-${practitioner.id}`,
                    title: `${practitioner.firstName} ${practitioner.lastName}`,
                    storeId: practitioner.storeId,
                    storeIds: practitioner.practitioner_store_practitioner_store_practitionerTopractitioner.map((assoc) => assoc.store_practitioner_store_storeTostore.id),
                    order: idx,
                    eventCount: practitioner._count.appointmentStepAssociations,
                    model: 'practitioners',
                    modelEntryId: practitioner.id,
                })),
        
                // Rooms (alphabetically sorted)
                ...sortedRooms.map((room, idx) => ({
                    id: `room-${room.id}`,
                    title: room.name,
                    storeId: room.storeId,
                    storeIds: [room.storeId],
                    order: idx + sortedPractitioners.length,
                    eventCount: room._count.appointmentStepAssociations,
                    model: 'rooms',
                    modelEntryId: room.id,
                })),
        
                // Equipment (alphabetically sorted)
                ...sortedEquipment.map((item, idx) => ({
                    id: `equipment-${item.id}`,
                    title: item.name,
                    storeId: item.storeId,
                    storeIds: [item.storeId],
                    order: idx + sortedPractitioners.length + sortedRooms.length,
                    eventCount: item._count.appointmentStepAssociations,
                    model: 'equipment',
                    modelEntryId: item.id,
                })),
            ];
        
            return formattedResources;
        }
    }, [resourcesData, configData, selectedStore]);

    // Update store with resources when they change
    useEffect(() => {
        if (resourcesData) {
            console.log("resourcesData")
            console.log(resourcesData)
            // Set all practitioners in the store
            if (resourcesData.practitioners && resourcesData.practitioners.length > 0) {
                setAllPractitioners(resourcesData.practitioners.map((practitioner) => ({
                    ...practitioner,
                    storeIds: practitioner.practitioner_store_practitioner_store_practitionerTopractitioner.map((assoc) => assoc.store_practitioner_store_storeTostore.id)
                })));
            }

            // Set all rooms in the store
            if (resourcesData.rooms && resourcesData.rooms.length > 0) {
                setAllRooms(resourcesData.rooms);
            }

            // Set all equipment in the store
            if (resourcesData.equipment && resourcesData.equipment.length > 0) {
                setAllEquipment(resourcesData.equipment);
            }
        }
    }, [resourcesData]);

    return (
        <Content>
            <div>{resources.length > 0 && <ResourceCalendar resources={resources} />}</div>
        </Content>
    );
};

export default AppointmentsCalendar;
